import React from 'react';
import {
  Button, Flex, Switch, TimePicker, Typography,
} from 'antd';
import { CloseOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { POSHUB_WEEKDAYS } from './constants';
import { formatPosHubTimeForApi, parsePosHubTimeString, POSHUB_TIME_DISPLAY_FORMAT } from './poshubTimeFormat';

/**
 * Fixed column widths (no 1fr) so rows stay compact instead of stretching edge-to-edge.
 * day | start | end | actions | closed
 */
export const POSHUB_HOURS_GRID_COLS = '108px 116px 116px 36px 96px';

const GRID_WRAP_STYLE = {
  maxWidth: 560,
  width: '100%',
  overflowX: 'auto',
};

/** Wide enough for rc-picker hour + minute + second columns side by side */
const TIME_PICKER_POPUP_STYLE = { minWidth: 280 };

function PosHubWeeklyHoursGrid({
  value,
  onChange,
  disabled = false,
}) {
  const schedule = value || {};

  const patchDay = (day, patch) => {
    onChange({
      ...schedule,
      [day]: { ...(schedule[day] || {}), ...patch },
    });
  };

  return (
    <div className="poshub-weekly-hours-grid-wrap" style={GRID_WRAP_STYLE}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: POSHUB_HOURS_GRID_COLS,
          gap: '12px 14px',
          alignItems: 'center',
          marginBottom: 10,
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'rgba(0,0,0,0.5)',
        }}
      >
        <span style={{ fontWeight: 700 }}>Day</span>
        <span style={{ fontWeight: 700 }}>Start time</span>
        <span style={{ fontWeight: 700 }}>End time</span>
        <span />
        <span />
      </div>
      {POSHUB_WEEKDAYS.map((day) => {
        const dayState = schedule[day] || { closed: true, timePeriods: [] };
        const { closed, timePeriods = [] } = dayState;
        const periods = !closed && timePeriods.length ? timePeriods : [];
        const displayRows = periods.length ? periods : [{ startTime: '', endTime: '' }];
        return (
          <div key={day} style={{ marginBottom: 8 }}>
            {displayRows.map((period, idx) => (
              <div
                key={`${day}-${idx}`} // eslint-disable-line react/no-array-index-key -- ordered slots, no stable id
                style={{
                  display: 'grid',
                  gridTemplateColumns: POSHUB_HOURS_GRID_COLS,
                  gap: '12px 14px',
                  alignItems: 'center',
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    color: 'rgba(0,0,0,0.65)',
                  }}
                >
                  {idx === 0 ? day : ''}
                </span>
                <TimePicker
                  format={POSHUB_TIME_DISPLAY_FORMAT}
                  use12Hours
                  needConfirm={false}
                  showSecond={false}
                  minuteStep={1}
                  popupStyle={TIME_PICKER_POPUP_STYLE}
                  placeholder="Start time"
                  value={parsePosHubTimeString(period.startTime)}
                  onChange={(val) => {
                    const next = [...(dayState.timePeriods?.length ? dayState.timePeriods : [{ startTime: '', endTime: '' }])];
                    if (!next[idx]) next[idx] = { startTime: '', endTime: '' };
                    next[idx] = { ...next[idx], startTime: formatPosHubTimeForApi(val) };
                    patchDay(day, { timePeriods: next });
                  }}
                  style={{ width: '100%', maxWidth: 124, fontSize: 13 }}
                  size="middle"
                  disabled={disabled || closed}
                />
                <TimePicker
                  format={POSHUB_TIME_DISPLAY_FORMAT}
                  use12Hours
                  needConfirm={false}
                  showSecond={false}
                  minuteStep={1}
                  popupStyle={TIME_PICKER_POPUP_STYLE}
                  placeholder="End time"
                  value={parsePosHubTimeString(period.endTime)}
                  onChange={(val) => {
                    const next = [...(dayState.timePeriods?.length ? dayState.timePeriods : [{ startTime: '', endTime: '' }])];
                    if (!next[idx]) next[idx] = { startTime: '', endTime: '' };
                    next[idx] = { ...next[idx], endTime: formatPosHubTimeForApi(val) };
                    patchDay(day, { timePeriods: next });
                  }}
                  style={{ width: '100%', maxWidth: 124, fontSize: 13 }}
                  size="middle"
                  disabled={disabled || closed}
                />
                <span style={{ display: 'flex', gap: 4 }}>
                  {idx === 0 && !closed && (
                    <Button
                      type="text"
                      size="small"
                      icon={<PlusOutlined />}
                      disabled={disabled}
                      onClick={() => patchDay(day, {
                        timePeriods: [...(dayState.timePeriods || []), { startTime: '', endTime: '' }],
                      })}
                      aria-label="Add time slot"
                    />
                  )}
                  {idx > 0 && (
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<MinusOutlined />}
                      disabled={disabled}
                      onClick={() => patchDay(day, {
                        timePeriods: (dayState.timePeriods || []).filter((_, i) => i !== idx),
                      })}
                      aria-label="Remove time slot"
                    />
                  )}
                </span>
                {idx === 0 ? (
                  <Flex align="center" gap={6}>
                    <Switch
                      checked={closed}
                      disabled={disabled}
                      checkedChildren={<CloseOutlined />}
                      unCheckedChildren={null}
                      onChange={(isClosed) => {
                        let timePeriods;
                        if (isClosed) {
                          timePeriods = [];
                        } else if (dayState.timePeriods?.length) {
                          timePeriods = dayState.timePeriods;
                        } else {
                          timePeriods = [{ startTime: '', endTime: '' }];
                        }
                        patchDay(day, { closed: isClosed, timePeriods });
                      }}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Closed
                    </Typography.Text>
                  </Flex>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default PosHubWeeklyHoursGrid;
